import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getCookie, setCookie, deleteCookie } from 'src/utils/cookie';

interface UTMData {
    utmParams: string | null;
    timestamp: number | null;
    expiryDate: number | null;
    source?: string | null;
    medium?: string | null;
    campaign?: string | null;
    term?: string | null;
    content?: string | null;
    firstCaptureDate?: number | null;
}

interface UTMStore {
    utm: UTMData;
    setUTM: (utmString: string) => void;
    getUTM: () => string | null;
    clearUTM: () => void;
    syncFromCookie: () => void;
    shouldPropagateUTM: () => boolean;
    enableUTMPropagation: () => void;
    disableUTMPropagation: () => void;
    getSessionUTM: () => string | null;
    setSessionUTM: (utmString: string) => void;
    clearSessionUTM: () => void;
    hasSessionUTM: () => boolean;
}

const SESSION_UTM_KEY = 'uticket-session-utm';
const UTM_PROPAGATION_KEY = 'uticket-utm-propagation-active';
// Importando a constante para garantir consistência
const PASSED_ALLOWED_PAGE_KEY = 'uticket-passed-allowed-page';

const parseUTMParams = (queryString: string): UTMData => {
    const params = new URLSearchParams(queryString);
    const now = Date.now();
    const expiryDays = 90;
    const expiryDate = now + expiryDays * 24 * 60 * 60 * 1000;

    return {
        utmParams: queryString,
        timestamp: now,
        expiryDate: expiryDate,
        source: params.get('utm_source'),
        medium: params.get('utm_medium'),
        campaign: params.get('utm_campaign'),
        term: params.get('utm_term'),
        content: params.get('utm_content'),
        firstCaptureDate: now
    };
};


const hasAnyUtmValue = (utm: UTMData): boolean => {
    return utm.utmParams !== null || 
           utm.timestamp !== null || 
           utm.expiryDate !== null ||
           utm.source !== null ||
           utm.medium !== null ||
           utm.campaign !== null ||
           utm.term !== null ||
           utm.content !== null ||
           utm.firstCaptureDate !== null;
};

const useUtmStore = create<UTMStore>()(
    persist(
        (set, get) => ({
            utm: {
                utmParams: null,
                timestamp: null,
                expiryDate: null,
                source: null,
                medium: null,
                campaign: null,
                term: null,
                content: null,
                firstCaptureDate: null
            },
            setUTM: (utmString) => {
                const { utm } = get();
                const hasValidUtm = utm.utmParams && utm.expiryDate && utm.expiryDate > Date.now();
                
                if (hasValidUtm) {
                    sessionStorage.setItem(UTM_PROPAGATION_KEY, 'true');
                    return;
                }
                const utmData = parseUTMParams(utmString);
                const hostname = window.location.hostname;
                const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
                const isPrEnvironment = hostname === 'pr.uticket.dev.br' || hostname.endsWith('.pr.uticket.dev.br');
                const isDevEnvironment = hostname === 'uticket.dev.br' || (hostname.includes('uticket.dev.br') && !isPrEnvironment);
                const isProdEnvironment = hostname.includes('uticket.com.br');

                if (isLocalhost) {
                    deleteCookie('uticketUtm');
                    setCookie('uticketUtm', utmString, 90);
                } else if (isPrEnvironment) {
                    deleteCookie('uticketUtm');
                    deleteCookie('uticketUtm', '.pr.uticket.dev.br');
                    setCookie('uticketUtm', utmString, 90, '.pr.uticket.dev.br');
                } else if (isDevEnvironment) {
                    deleteCookie('uticketUtm');
                    deleteCookie('uticketUtm', '.uticket.dev.br');
                    setCookie('uticketUtm', utmString, 90, '.uticket.dev.br');
                } else if (isProdEnvironment) {
                    deleteCookie('uticketUtm');
                    deleteCookie('uticketUtm', '.uticket.com.br');
                    setCookie('uticketUtm', utmString, 90, '.uticket.com.br');
                }
                sessionStorage.setItem(UTM_PROPAGATION_KEY, 'true');

                sessionStorage.removeItem(SESSION_UTM_KEY);

                set({ utm: utmData });
            },
            getUTM: () => {
                const { utm } = get();
                if (utm.expiryDate && utm.expiryDate < Date.now()) {
                    get().clearUTM();
                    return null;
                }

                return utm.utmParams;
            },
            clearUTM: () => {
                set({
                    utm: {
                        utmParams: null,
                        timestamp: null,
                        expiryDate: null,
                        source: null,
                        medium: null,
                        campaign: null,
                        term: null,
                        content: null,
                        firstCaptureDate: null
                    }
                });
                sessionStorage.removeItem(UTM_PROPAGATION_KEY);
                sessionStorage.removeItem(SESSION_UTM_KEY);
                // Limpar também a flag de página permitida quando limpar todos os UTMs
                sessionStorage.removeItem(PASSED_ALLOWED_PAGE_KEY);
                
                localStorage.removeItem('uticket-utm-storage');
                
                const hostname = window.location.hostname;
                const isPrEnvironment = hostname === 'pr.uticket.dev.br' || hostname.endsWith('.pr.uticket.dev.br');
                const isDevEnvironment = hostname === 'uticket.dev.br' || (hostname.includes('uticket.dev.br') && !isPrEnvironment);
                const isProdEnvironment = hostname.includes('uticket.com.br');
                
                deleteCookie('uticketUtm');
                
                if (isPrEnvironment) {
                    deleteCookie('uticketUtm', '.pr.uticket.dev.br');
                } else if (isDevEnvironment) {
                    deleteCookie('uticketUtm', '.uticket.dev.br');
                } else if (isProdEnvironment) {
                    deleteCookie('uticketUtm', '.uticket.com.br');
                }
            },
            syncFromCookie: () => {
                const cookieUtm = getCookie('uticketUtm');
                const { utm } = get();

                const hasValidUtm = utm.utmParams && utm.expiryDate && utm.expiryDate > Date.now();
                
                // Caso 1: Cookie existe, não temos UTM válida no localStorage
                if (cookieUtm && !hasValidUtm) {
                    const utmData = parseUTMParams(cookieUtm);
                    if (utm.firstCaptureDate) {
                        utmData.firstCaptureDate = utm.firstCaptureDate;
                    }
                    set({ utm: utmData });
                    return;
                }
                
                // Caso 2: localStorage tem dados válidos, mas cookie não existe
                if (hasValidUtm && !cookieUtm) {
                    const hostname = window.location.hostname;
                    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
                    const isPrEnvironment = hostname === 'pr.uticket.dev.br' || hostname.endsWith('.pr.uticket.dev.br');
                    const isDevEnvironment = hostname === 'uticket.dev.br' || (hostname.includes('uticket.dev.br') && !isPrEnvironment);
                    const isProdEnvironment = hostname.includes('uticket.com.br');

                    if (isLocalhost) {
                        deleteCookie('uticketUtm');
                        setCookie('uticketUtm', utm.utmParams!, 90);
                    } else if (isPrEnvironment) {
                        deleteCookie('uticketUtm');
                        deleteCookie('uticketUtm', '.pr.uticket.dev.br');
                        setCookie('uticketUtm', utm.utmParams!, 90, '.pr.uticket.dev.br');
                    } else if (isDevEnvironment) {
                        deleteCookie('uticketUtm');
                        deleteCookie('uticketUtm', '.uticket.dev.br');
                        setCookie('uticketUtm', utm.utmParams!, 90, '.uticket.dev.br');
                    } else if (isProdEnvironment) {
                        deleteCookie('uticketUtm');
                        deleteCookie('uticketUtm', '.uticket.com.br');
                        setCookie('uticketUtm', utm.utmParams!, 90, '.uticket.com.br');
                    }
                }
            },
            shouldPropagateUTM: () => {
                return sessionStorage.getItem(UTM_PROPAGATION_KEY) === 'true';
            },
            enableUTMPropagation: () => {
                sessionStorage.setItem(UTM_PROPAGATION_KEY, 'true');
            },
            disableUTMPropagation: () => {
                sessionStorage.removeItem(UTM_PROPAGATION_KEY);
            },
            getSessionUTM: () => {
                return sessionStorage.getItem(SESSION_UTM_KEY);
            },
            setSessionUTM: (utmString) => {
                sessionStorage.setItem(SESSION_UTM_KEY, utmString);
                sessionStorage.setItem(UTM_PROPAGATION_KEY, 'true');
            },
            clearSessionUTM: () => {
                // Limpar apenas o UTM da sessão, mas manter a flag de página permitida
                sessionStorage.removeItem(SESSION_UTM_KEY);
            },
            hasSessionUTM: () => {
                return sessionStorage.getItem(SESSION_UTM_KEY) !== null;
            }
        }),
        {
            name: 'uticket-utm-storage',
            getStorage: () => localStorage,
            partialize: (state) => hasAnyUtmValue(state.utm) ? state : {},
        }
    )
);

export default useUtmStore; 